<template>
  <div v-if="showfeature" class="content-dashboard">
    <div class="row">
      <div class="col-lg-7">
        <h3>Planifiez vos communications avec vos clients</h3>
        <p>
          Sélectionnez les newsletters à envoyer à vos clients pour renforcer leur engagement.
        </p>
      </div>
    </div>

    <!-- Bulk Action Section -->
    <div class="row mt-4">
      <div class="col-lg-12">
        <div class="bulk-action-box p-4 bg-light rounded">
          <h5 class="mb-3">Action groupée par espèce</h5>
          <div class="row g-3 align-items-end">
            <div class="col-lg-4 col-md-6">
              <label for="speciesSelect" class="form-label">Espèce</label>
              <select
                id="speciesSelect"
                v-model="selectedSpecies"
                class="form-select"
                :disabled="loadingSpecies"
              >
                <option value="">Sélectionner l'espèce</option>
                <option
                  v-for="species in speciesList"
                  :key="species.id"
                  :value="species.id"
                >
                  {{ species.name || species.titre || species.label }}
                </option>
              </select>
            </div>

            <div class="col-lg-4 col-md-6">
              <label for="actionSelect" class="form-label">Action</label>
              <select
                id="actionSelect"
                v-model="selectedAction"
                class="form-select"
              >
                <option value="">Sélectionner une action</option>
                <option value="true">Activer</option>
                <option value="false">Désactiver</option>
              </select>
            </div>

            <div class="col-lg-2 col-md-12">
              <button
                @click="handleBulkAction"
                class="btn btn-orange"
                :disabled="!selectedSpecies || selectedAction === '' || isProcessingBulk"
              >
                Valider
                <span v-if="isProcessingBulk" class="spinner-border spinner-border-sm me-2"></span>
              </button>
            </div>
          </div>

          <!-- Success/Error Messages -->
          <div v-if="bulkActionMessage" class="mt-3">
            <div
              :class="['alert', bulkActionSuccess ? 'alert-success' : 'alert-danger']"
              role="alert"
            >
              <i :class="['ti', bulkActionSuccess ? 'ti-check' : 'ti-alert-circle']"></i>
              {{ bulkActionMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-5">
      <div class="col-lg-12">
        <div class="box-chart mb-3 w-100">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>
          <div v-else-if="error" class="alert alert-danger mt-4" role="alert">
            <i class="ti ti-alert-circle"></i> {{ error }}
            <button @click="fetchNewsletters" class="btn btn-sm btn-outline-danger ms-3">
              Réessayer
            </button>
          </div>
          <div v-else-if="!newsletters.length" class="text-center py-5 text-muted">
            <i class="ti ti-inbox" style="font-size: 3rem;"></i>
            <p class="mt-3">Aucune newsletter de communication disponible pour le moment.</p>
          </div>
          <div v-else class="table-responsive mt-4">
            <table class="table table-style-1">
              <thead>
                <tr>
                  <th><strong>Nom de la newsletter</strong></th>
                  <th><strong>Espèce ciblée</strong></th>
                  <th><strong>Aperçu</strong></th>
                  <th><strong>Date d'envoi</strong></th>
                  <th><strong>Statut</strong></th>
                  <th><strong>Prévisualiser</strong></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="newsletter in newsletters" :key="newsletter.id">
                  <td class="w-25 blue-color font-bold">
                    {{ newsletter.titre }}
                  </td>
                  <td>{{ newsletter.espece }}</td>
                  <td>
                    <img
                      v-if="newsletter.thumbnail"
                      :src="newsletter.thumbnail"
                      :alt="newsletter.titre"
                      class="preview-image"
                      loading="lazy"
                    />
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>{{ newsletter.date }}</td>
                  <td>
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'switch' + newsletter.id"
                        :checked="newsletter.statut"
                        :disabled="newsletter.isUpdating"
                        @change="toggleStatus(newsletter)"
                      />
                      <span v-if="newsletter.isUpdating" class="spinner-border spinner-border-sm ms-2"></span>
                    </div>
                  </td>
                  <td >
                    <button
                      class="btn-save"
                      @click="previewNewsletter(newsletter)"
                      :title="'Voir ' + newsletter.titre"
                    >
                      <i class="ti ti-eye"></i>
                    </button>

                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="fixed inset-0 flex items-center justify-center bg-white margin20">
    <p class="text-lg italic font-italic text-gray-700 text-center max-w-md">
     Cette fonctionnalité sera déployée prochainement. Nous prendrons contact avec vous afin de vous la présenter plus en détails, ainsi que les newsletters proposées pour l'année à venir.
    </p>
  </div>

  <!-- Full Screen Modal Overlay -->
  <div v-if="showModal" class="fullscreen-modal-overlay" @click.self="closeModal">
    <button class="fullscreen-close-btn" @click="closeModal" aria-label="Fermer">&times;</button>
    <div class="fullscreen-modal-content">
      <img
        :src="modalImage"
        alt="Preview"
        class="fullscreen-modal-image"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import {
  fetchCommunications as fetchNewslettersAPI,
  changeCommunicationStatus,
  fetchCommunicationCategories,
  toggleStatusByCategorie
} from "@/api/veterinaireService";


export default {
  name: "NewsletterView",
  setup() {
    const newsletters = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const showfeature = ref(isFeatureAvailable());

    // Bulk action state
    const speciesList = ref([]);
    const loadingSpecies = ref(false);
    const selectedSpecies = ref("");
    const selectedAction = ref("");
    const isProcessingBulk = ref(false);
    const bulkActionMessage = ref("");
    const bulkActionSuccess = ref(false);

    function isFeatureAvailable() {
      const today = new Date();
     //const releaseDate = new Date(2027, 0, 1); 
      const releaseDate = new Date(2026, 0, 1); 
      return today >= releaseDate;
    }

    // Modal state
    const showModal = ref(false);
    const modalImage = ref(null);

    // Fetch newsletters from API
    const fetchNewsletters = async () => {
      loading.value = true;
      error.value = null;

      try {
        const data = await fetchNewslettersAPI();
        newsletters.value = data.map(item => ({
          id: item.id,
          titre: item.title || "Sans titre",
          espece: item.categorie || "-",
          thumbnail: item.image?.thumbnail || null,
          imageOriginal: item.image?.original || null,
          date: formatDate(item.sendingDateTime),
          statut: item.enabled_veterinary || false,
          isUpdating: false,
        }));
      } catch (err) {
        error.value = err.message || "Une erreur est survenue lors du chargement des newsletters";
        console.error("Error fetching newsletters:", err);
      } finally {
        loading.value = false;
      }
    };

    // Fetch species for bulk action
    const fetchSpecies = async () => {
      loadingSpecies.value = true;
      try {
        const data = await fetchCommunicationCategories();
        speciesList.value = data;
      } catch (err) {
        console.error("Error fetching species:", err);
        // Silently fail for species - non-critical
      } finally {
        loadingSpecies.value = false;
      }
    };

    // Handle bulk action
    const handleBulkAction = async () => {
      if (!selectedSpecies.value || selectedAction.value === "") {
        bulkActionMessage.value = "Veuillez sélectionner une espèce et une action";
        bulkActionSuccess.value = false;
        setTimeout(() => { bulkActionMessage.value = ""; }, 3000);
        return;
      }

      isProcessingBulk.value = true;
      bulkActionMessage.value = "";

      try {
        const enabledValue = selectedAction.value === "true";
        await toggleStatusByCategorie(selectedSpecies.value, enabledValue);

        bulkActionSuccess.value = true;
        bulkActionMessage.value = `Action réussie : toutes les newsletters de l'espèce ont été ${enabledValue ? 'activées' : 'désactivées'}`;

        // Refresh the newsletters list
        await fetchNewsletters();

        // Reset form
        selectedSpecies.value = "";
        selectedAction.value = "";
      } catch (err) {
        bulkActionSuccess.value = false;
        bulkActionMessage.value = err.message || "Une erreur est survenue lors de l'action groupée";
      } finally {
        isProcessingBulk.value = false;
        setTimeout(() => { bulkActionMessage.value = ""; }, 5000);
      }
    };

    // Toggle the status of a newsletter
    const toggleStatus = async (newsletter) => {
      const originalStatus = newsletter.statut;
      newsletter.statut = !newsletter.statut;
      newsletter.isUpdating = true;
      try {
        await changeCommunicationStatus(newsletter.id, newsletter.statut);
      } catch (err) {
        newsletter.statut = originalStatus;
        error.value = `Erreur lors du changement de statut: ${err.message}`;
        setTimeout(() => {
          error.value = null;
        }, 5000);
      } finally {
        newsletter.isUpdating = false;
      }
    };

    // Date formatting: "YYYY-MM-DD HH:mm:ss" -> French locale
    const formatDate = (dateString) => {
      if (!dateString) return "-";
      try {
        const [datePart, timePart] = dateString.split(" ");
        const [year, month, day] = datePart.split("-");
        const [hours, minutes] = timePart.split(":");
        const dateObj = new Date(year, month - 1, day, hours, minutes);
        return dateObj.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return dateString;
      }
    };

    // Show modal with image when user clicks on eye icon
    const previewNewsletter = (newsletter) => {
      if (newsletter.imageOriginal) {
        modalImage.value = newsletter.imageOriginal;
        showModal.value = true;
        if (typeof window !== "undefined" && window.document && window.document.body)
          window.document.body.style.overflow = "hidden";
      }
    };

    const closeModal = () => {
      showModal.value = false;
      modalImage.value = null;
      if (typeof window !== "undefined" && window.document && window.document.body)
        window.document.body.style.overflow = "";
    };

    // Download image logic
    const downloadImage = async (url, filename) => {
      console.log("Downloading image from URL:", url);
      console.log("Using filename:", filename);
 
};


    onMounted(() => {
      fetchNewsletters();
      fetchSpecies();
    });

    return {
      newsletters,
      loading,
      error,
      showfeature,
      fetchNewsletters,
      toggleStatus,
      formatDate,
      showModal,
      modalImage,
      previewNewsletter,
      closeModal,
      // Bulk action
      speciesList,
      loadingSpecies,
      selectedSpecies,
      selectedAction,
      isProcessingBulk,
      bulkActionMessage,
      bulkActionSuccess,
      handleBulkAction,
      downloadImage
    };
  },
};
</script>

<style scoped>
.font-italic {
  font-style: italic !important;
}

.margin20 {
  margin: 20%;
}

/* Bulk Action Styles */
.bulk-action-box {
  border: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.bulk-action-box h5 {
  color: #333;
  font-weight: 600;
  margin-bottom: 1rem;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Table Styles */
.preview-image {
  max-width: 80px;
  max-height: 60px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.preview-image:hover {
  transform: scale(1.05);
}

.btn-save {
  transition: all 0.3s ease;
}

.btn-save:hover {
  transform: scale(1.1);
}

.btn-download {
  display: inline-block;
  margin-left: 4px;
  padding: 6px 10px;
  border: none;
  background: #FFA500;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.2s;
}

.btn-download:hover {
  background: #e88009;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.blue-color {
  color: #0066cc;
}

.font-bold {
  font-weight: 600;
}

/* Fullscreen Modal Styles */
.fullscreen-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.fullscreen-close-btn {
  position: fixed;
  top: 32px;
  right: 48px;
  font-size: 3rem;
  font-weight: 700;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  z-index: 1010;
  opacity: 0.85;
  transition: opacity 0.2s;
}

.fullscreen-close-btn:hover {
  opacity: 1;
}

.fullscreen-modal-content {
  position: relative;
  max-width: 96vw;
  max-height: 88vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-modal-image {
  max-width: 92vw;
  max-height: 84vh;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
  background: #222;
  object-fit: contain;
  margin: auto;
}

.btn.btn-orange {
  padding: 4px 20px;
}

</style>
